/**
 * Core domain model. These shapes are persisted verbatim as JSON blobs, so any
 * change here is a storage migration — see `docs/data-model.md`.
 */

export type Iso8601 = string;

// ---------------------------------------------------------------------------
// Script structure
// ---------------------------------------------------------------------------

export type LineKind = 'dialogue' | 'direction';

export interface Line {
  lineId: string;
  /** Normalized character key. Empty string for stage directions. */
  character: string;
  text: string;
  kind: LineKind;
  /** Position within the owning scene, 0-based and contiguous. */
  order: number;
}

export interface Scene {
  sceneId: string;
  name: string;
  lines: Line[];
}

export type CharacterGender = 'female' | 'male' | 'neutral';

export interface Character {
  /** Normalized, uppercase, punctuation-stripped key used for all lookups. */
  id: string;
  /** Display name in title case, e.g. "Mistress Ford". */
  name: string;
  /** Raw spellings collapsed into this character during parsing. */
  aliases: string[];
  lineCount: number;
  /** Chooses the voice pool to read this character from. Absent on older shows. */
  gender?: CharacterGender;
}

export interface Script {
  showId: string;
  title: string;
  characters: Character[];
  scenes: Scene[];
  updatedAt: Iso8601;
}

// ---------------------------------------------------------------------------
// Shows
// ---------------------------------------------------------------------------

export interface ShowSummary {
  showId: string;
  /** Characters the user plays; one actor often doubles several parts. */
  myRoles: string[];
  title: string;
  lineCount: number;
  myLineCount: number;
  sceneCount: number;
  createdAt: Iso8601;
  updatedAt: Iso8601;
}

export interface Show extends ShowSummary {
  /** Per-character voice overrides, keyed by character id. */
  voices: Record<string, VoiceAssignment>;
}

// ---------------------------------------------------------------------------
// Voices
// ---------------------------------------------------------------------------

export interface VoiceAssignment {
  /** `SpeechSynthesisVoice.voiceURI`, or null to use the engine default. */
  voiceURI: string | null;
  pitch: number;
  rate: number;
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export type FeedbackKind = 'idea' | 'bug';

/** One entry on the board every signed-in user shares. */
export interface FeedbackItem {
  id: string;
  kind: FeedbackKind;
  title: string;
  detail: string;
  authorId: string;
  authorName: string;
  votes: string[];
  createdAt: Iso8601;
}

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

export type AttemptResult = 'correct' | 'hesitated' | 'revealed' | 'missed';

export interface LineProgress {
  attempts: number;
  correct: number;
  revealed: number;
  hesitated: number;
  missed: number;
  lastResult: AttemptResult | null;
  lastPracticedAt: Iso8601 | null;
  /** Monotonic clock used for last-write-wins merges across devices. */
  updatedAt: Iso8601;
}

export interface ShowProgress {
  showId: string;
  lineStats: Record<string, LineProgress>;
  updatedAt: Iso8601;
}

export interface LineNote {
  note: string;
  translation: string;
  coaching: string;
  updatedAt: Iso8601;
}

export type ShowNotes = Record<string, LineNote>;

// ---------------------------------------------------------------------------
// Sessions & stats
// ---------------------------------------------------------------------------

export type RehearsalMode = 'cue' | 'scene' | 'speed' | 'missed' | 'flashcards' | 'quiz';

export interface Attempt {
  lineId: string;
  sceneId: string;
  result: AttemptResult;
  /** Milliseconds between the cue finishing and the user responding. */
  responseMs: number;
}

export interface PracticeSession {
  sessionId: string;
  showId: string;
  mode: RehearsalMode;
  sceneIds: string[];
  startedAt: Iso8601;
  endedAt: Iso8601;
  durationMs: number;
  /**
   * `Date.prototype.getTimezoneOffset()` at the time of the session, i.e.
   * minutes to subtract from UTC to get local time. The server needs this to
   * bucket practice into the same calendar day the user saw.
   */
  utcOffsetMinutes: number;
  attempts: Attempt[];
}

export interface UserStats {
  totalPracticeMs: number;
  currentStreak: number;
  longestStreak: number;
  lastPracticeDate: string | null;
  /** Local calendar date (yyyy-MM-dd) -> milliseconds practiced. */
  dailyMs: Record<string, number>;
  updatedAt: Iso8601;
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export type ThemeMode = 'light' | 'dark' | 'system';
export type FontScale = 'sm' | 'md' | 'lg' | 'xl';

export interface UserSettings {
  /** Speech rate multiplier applied on top of per-character rate. */
  rehearsalSpeed: number;
  /** Milliseconds of silence after a cue before auto-advance in speed mode. */
  pauseMs: number;
  /** Response time above which an otherwise-correct attempt counts as a hesitation. */
  hesitationThresholdMs: number;
  /** Number of trailing words of the preceding line used as a cue. */
  cueWordCount: number;
  volume: number;
  defaultVoiceURI: string | null;
  theme: ThemeMode;
  fontScale: FontScale;
  highContrast: boolean;
  reduceMotion: boolean;
  autoAdvance: boolean;
  speakStageDirections: boolean;
  /** Browser speech recognition grades each line and advances when it matches. */
  listeningEnabled: boolean;
  aiEnabled: boolean;
}

export interface UserProfile {
  userId: string;
  displayName: string;
  email: string;
  createdAt: Iso8601;
  settings: UserSettings;
}

export const DEFAULT_SETTINGS: UserSettings = {
  rehearsalSpeed: 1,
  pauseMs: 1500,
  hesitationThresholdMs: 4000,
  cueWordCount: 6,
  volume: 1,
  defaultVoiceURI: null,
  theme: 'system',
  fontScale: 'md',
  highContrast: false,
  reduceMotion: false,
  autoAdvance: false,
  speakStageDirections: false,
  listeningEnabled: true,
  aiEnabled: false,
};

export const EMPTY_LINE_PROGRESS: Omit<LineProgress, 'updatedAt'> = {
  attempts: 0,
  correct: 0,
  revealed: 0,
  hesitated: 0,
  missed: 0,
  lastResult: null,
  lastPracticedAt: null,
};
